import React from "react";
import {
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Divider,
  Stack,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { IncomeReportData } from "../types";
import { formatCurrency, formatDate } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";

interface DetailedIncomesProps {
  data: IncomeReportData;
}

const DetailedIncomes: React.FC<DetailedIncomesProps> = ({ data }) => {
  const [page, setPage] = React.useState(1);
  const [expandedPanel, setExpandedPanel] = React.useState<string | false>(
    false
  );
  const rowsPerPage = 10;

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleAccordionChange =
    (tripId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? tripId : false);
    };

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedTrips = data.detailedTrips.slice(startIndex, endIndex);
  const navigate = useNavigate();

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom fontSize={16}>
        Detailed Incomes
      </Typography>
      <Box sx={{ mt: 2 }}>
        {displayedTrips.map((trip) => (
          <Accordion
            key={trip.tripId}
            expanded={expandedPanel === trip.tripId}
            onChange={handleAccordionChange(trip.tripId)}
            sx={{
              mb: 1,
              "&:before": {
                display: "none",
              },
              boxShadow: "none",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <AccordionSummary
              sx={{
                "& .MuiAccordionSummary-content": {
                  justifyContent: "space-between",
                  alignItems: "center",
                },
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontSize: "12px",
                      cursor: "pointer",
                      textDecoration: "underline",
                      color: "primary.main",
                    }}
                    onClick={() => {
                      navigate(`/trip/daily-bus/${trip.tripId}/basic-details`);
                    }}
                  >
                    {trip.fromLocation} → {trip.toLocation} : {trip.tripType}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>
                    Start: {formatDate(trip.journeyStartDate)}
                    {trip.journeyReturnDate && (
                      <>
                        {" - "}Return: {formatDate(trip.journeyReturnDate)}
                      </>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ fontSize: "12px" }}
                  >
                    Net Income: {formatCurrency(trip.totalIncome)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={1}>
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      fontSize: "12px",
                    }}
                  >
                    {expandedPanel === trip.tripId ? "Hide" : "Show Details"}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 0 }}>
              <Stack spacing={2} divider={<Divider />}>
                {trip.totalIncome === 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <Typography variant="subtitle1" color="text.secondary">
                      No Income
                    </Typography>
                  </Box>
                )}
                {trip.totalIncome > 0 && (
                  <Box sx={{ py: 2 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={tableHeaderStyle}>Source</TableCell>
                            <TableCell sx={tableHeaderStyle}>
                              Journey Type
                            </TableCell>
                            <TableCell sx={tableHeaderStyle}>
                              Booking Mode
                            </TableCell>
                            <TableCell sx={tableHeaderStyle}>Amount</TableCell>
                            <TableCell sx={tableHeaderStyle}>
                              Net Amount
                            </TableCell>
                            <TableCell sx={tableHeaderStyle}>
                              Commission Amount
                            </TableCell>
                            <TableCell sx={tableHeaderStyle}>
                              Received Amount
                            </TableCell>
                            <TableCell sx={tableHeaderStyle}>
                              Pending Amount
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {trip.BUS?.incomes.map((income, index) => (
                            <TableRow key={`bus-${index}`}>
                              <TableCell sx={tableCellStyle}>BUS</TableCell>
                              <TableCell sx={tableCellStyle}>
                                {income.journeyType}
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                {income.bookingMode}
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="secondary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.amount)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="secondary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.totalNetAmount)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="primary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.totalCommissionAmount)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="secondary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.totalReceivedAmount)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="primary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.totalPendingAmount)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          {trip.LUGGAGE?.incomes.map((income, index) => (
                            <TableRow key={`luggage-${index}`}>
                              <TableCell sx={tableCellStyle}>LUGGAGE</TableCell>
                              <TableCell sx={tableCellStyle}>
                                {income.journeyType}
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                {income.bookingMode}
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="secondary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.amount)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="secondary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.totalNetAmount)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="primary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.totalCommissionAmount)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="secondary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.totalReceivedAmount)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={tableCellStyle}>
                                <Typography
                                  variant="body1"
                                  fontWeight="bold"
                                  color="primary.main"
                                  fontSize={tableCellStyle.fontSize}
                                >
                                  {formatCurrency(income.totalPendingAmount)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {trip.BUS?.incomes.length &&
                      trip.BUS?.incomes.length > 0 && (
                        <>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                            sx={{ mb: 1, mt: 2 }}
                          >
                            Total Bus Income:{" "}
                            {formatCurrency(trip.BUS.summary.totalIncome)}
                          </Typography>
                        </>
                      )}
                    {trip.LUGGAGE?.incomes.length &&
                      trip.LUGGAGE?.incomes.length > 0 && (
                        <>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                            sx={{ mb: 2 }}
                          >
                            Total Luggage Income:{" "}
                            {formatCurrency(trip.LUGGAGE.summary.totalIncome)}
                          </Typography>
                        </>
                      )}
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      {Math.ceil(data.detailedTrips.length / rowsPerPage) > 1 && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={Math.ceil(data.detailedTrips.length / rowsPerPage)}
            page={page}
            onChange={handleChangePage}
            color="primary"
          />
        </Box>
      )}
    </Paper>
  );
};

export default DetailedIncomes;

const tableHeaderStyle = {
  padding: "12px 16px",
  textAlign: "left" as const,
  backgroundColor: "#f5f5f5",
  borderBottom: "1px solid #ddd",
  fontSize: "12px",
};

const tableCellStyle = {
  borderBottom: "1px solid #ddd",
  fontSize: "12px",
};
